import React from 'react';
import { styled, useTheme } from '@mui/material/styles';

import { Title, createLoadingShimmerStyles } from '@bloomays-lib/ui.shared';

import box from '../../assets/illustrations/box.svg';
import trophy from '../../assets/illustrations/trophy-rose.png';
import cible from '../../assets/illustrations/cible.png';
import loupe from '../../assets/illustrations/loupe-rose.png';
import loadingSvg from '../../assets/illustrations/loading.svg';

export type JobStatusCardProps = {
  title: string;
  description: string;
  imgName: 'box' | 'trophy' | 'cible' | 'loupe' | 'loading';
  loading: boolean;
};

const JobStatusCard = ({ title, description, imgName, loading }: JobStatusCardProps) => {
  const theme = useTheme();

  let img;

  switch (imgName) {
    case 'box':
      img = box;
      break;
    case 'trophy':
      img = trophy;
      break;
    case 'cible':
      img = cible;
      break;
    case 'loupe':
      img = loupe;
      break;
    case 'loading':
      img = loadingSvg;
      break;
  }

  if (loading) {
    title = '...';
    description = '...';
  }

  return (
    <Container loading={loading}>
      <TextContainer>
        <Title
          textTitle={title}
          variant="heading6"
          sx={{
            color: theme.palette.neutral.black,
            textAlign: 'left',
            fontSize: '48px',
            lineHeight: '39.2px',
            [theme.breakpoints.down('sm')]: {
              fontSize: '24px',
              lineHeight: '33.6px',
            },
          }}
        />
        <Description dangerouslySetInnerHTML={{ __html: description }} />
      </TextContainer>
      <IconContainer>
        <img src={img} alt="icon" />
      </IconContainer>
    </Container>
  );
};

const Container = styled('div', { shouldForwardProp: (prop) => prop !== 'loading' })<{ loading: boolean }>(
  ({ theme, loading }) => ({
    display: 'flex',
    width: '100%',
    height: '100%',
    minHeight: '102px',
    justifyContent: 'space-between',
    alignItems: 'center',
    background: theme.palette.gradient.red,
    borderRadius: '15px',
    padding: '30px 20px',
    [theme.breakpoints.down('sm')]: {
      padding: '16px',
    },
    ...createLoadingShimmerStyles({ active: loading, contentOpacity: 0.6, speedMs: 1200, lightOpacity: 0.6 }),
  }),
);

const TextContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  [theme.breakpoints.down('sm')]: {
    gap: '0px',
  },
}));

const Description = styled('div')(({ theme }) => ({
  fontFamily: '"Poppins Regular", sans-serif',
  '& strong, & b': {
    fontFamily: '"Poppins SemiBold", sans-serif',
    fontSize: '18px',
    [theme.breakpoints.down('sm')]: {
      fontSize: '16px',
    },
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '14px',
    lineHeight: '19.6px',
  },
}));

const IconContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '& img': {
    width: '140px',
    height: '140px',
    [theme.breakpoints.down('xl')]: {
      width: '120px',
      height: '120px',
    },
    [theme.breakpoints.down('md')]: {
      width: '110px',
      height: '110px',
    },
    [theme.breakpoints.down('sm')]: {
      width: '100px',
      height: '100px',
    },
  },
  [theme.breakpoints.down('sm')]: {
    marginTop: '14px',
    width: '100px',
    height: '100px',
  },
}));

export default JobStatusCard;
export { JobStatusCard };
